import * as React from 'react';
import { throttle } from './throttle';

function useEventCallback<T>(fn: (event: T) => void) {
  const ref = React.useRef(fn);
  React.useEffect(() => {
    ref.current = fn;
  });
  return React.useCallback((args: any /** Should really be T here... */) => ref.current(args), []);
}

function ownerDocument(node: Node | null): Document {
  return (node && node.ownerDocument) || document;
}
function asc(a: number, b: number) {
  return a - b;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(min, value), max);
}

function findClosest(values: number[], currentValue: number) {
  const { index: closestIndex } = values.reduce((acc: any, value: number, index: number) => {
    const distance = Math.abs(currentValue - value);

    if (acc === null || distance < acc.distance || distance === acc.distance) {
      return {
        distance,
        index,
      };
    }

    return acc;
  }, null);
  return closestIndex;
}

function isTouch(event: Event): event is TouchEvent {
  return Boolean((event as TouchEvent).changedTouches);
}

function trackFinger(event: Event, touchId: React.MutableRefObject<number | undefined>) {
  if (touchId.current !== undefined && isTouch(event)) {
    for (let i = 0; i < event.changedTouches.length; i += 1) {
      const touch = event.changedTouches[i];
      if (touch.identifier === touchId.current) {
        return {
          x: touch.clientX,
          y: touch.clientY,
        };
      }
    }

    return false;
  }

  return {
    x: (event as MouseEvent).clientX,
    y: (event as MouseEvent).clientY,
  };
}

export function valueToPercent(value: number, min: number, max: number) {
  return ((value - min) * 100) / (max - min);
}

function percentToValue(percent: number, min: number, max: number) {
  return (max - min) * percent + min;
}

function getDecimalPrecision(num: number) {
  // This handles the case when num is very small (0.00000001), js will turn this into 1e-8.
  // When num is bigger than 1 or less than -1 it won't get converted to this notation so it's fine.
  if (Math.abs(num) < 1) {
    const parts = num.toExponential().split('e-');
    const matissaDecimalPart = parts[0].split('.')[1];
    return (matissaDecimalPart ? matissaDecimalPart.length : 0) + parseInt(parts[1], 10);
  }

  const decimalPart = num.toString().split('.')[1];
  return decimalPart ? decimalPart.length : 0;
}

function roundValueToStep(value: number, step: number, min: number) {
  const nearest = Math.round((value - min) / step) * step + min;
  return Number(nearest.toFixed(getDecimalPrecision(step)));
}

function setValueIndex({
  values,
  source,
  newValue,
  index,
}: {
  values: number[];
  source: number[];
  newValue: number;
  index: number;
}) {
  // Performance shortcut
  if (source[index] === newValue) {
    return source;
  }

  const output = values.slice();
  output[index] = newValue;
  return output;
}

// TODO: remove support for Safari < 13.
// https://caniuse.com/#search=touch-action
//
// Safari, on iOS, supports touch action since v13.
// Over 80% of the iOS phones are compatible
// in August 2020.
let cachedSupportsTouchActionNone: boolean;
function doesSupportTouchActionNone() {
  if (cachedSupportsTouchActionNone === undefined) {
    const element = document.createElement('div');
    element.style.touchAction = 'none';
    document.body.appendChild(element);
    cachedSupportsTouchActionNone = window.getComputedStyle(element).touchAction === 'none';
    element.parentElement?.removeChild(element);
  }
  return cachedSupportsTouchActionNone;
}

export type PropType = {
  max: number;
  min: number;
  values: number[];
  onChange: (currentMin: number, currentMax: number) => void;
  disabled?: React.MutableRefObject<boolean>;
};

export function useRangeSlider(props: PropType) {
  const touchId = React.useRef<number | undefined>(undefined);
  const previousIndex = React.useRef<number>(0);
  const ref = React.useRef<HTMLSpanElement>(null);
  const { min, max } = props;
  const [valueDerived, setValueState] = React.useState<number[]>(props.values);
  const step = 1;
  const flipped = React.useRef<boolean>(false);

  let values: number[] = valueDerived.slice().sort(asc);
  values = values.map(value => clamp(value, min, max));

  React.useEffect(() => {
    setValueState(props.values);
  }, [props.values]);

  React.useEffect(() => {
    if (valueDerived[0] > valueDerived[1]) {
      flipped.current = !flipped.current;
    }
  });

  const getFingerNewValue = ({
    finger,
    move = false,
    values: values2,
    source,
  }: {
    finger: false | { x: number; y: number };
    move?: boolean;
    values: number[];
    source: number[];
  }) => {
    const { current: slider } = ref;
    const { width, left } = slider?.getBoundingClientRect() || {
      width: 0,
      left: 0,
    };

    const percent = ((finger as { x: number; y: number }).x - left) / width;

    let newValue;
    newValue = percentToValue(percent, min, max);
    if (step) {
      newValue = roundValueToStep(newValue, step, min);
    } else {
      const closestIndex = findClosest(values, newValue);
      newValue = values[closestIndex];
    }

    newValue = clamp(newValue, min, max);
    let activeIndex = 0;

    if (!move) {
      activeIndex = findClosest(values2, newValue);
    } else {
      activeIndex = previousIndex.current;
    }

    const previousValue = newValue;
    newValue = setValueIndex({
      values: values2,
      source,
      newValue,
      index: activeIndex,
    }).sort(asc);
    activeIndex = newValue.indexOf(previousValue);
    previousIndex.current = activeIndex;

    return { newValue, activeIndex };
  };

  const handleTouchEnd = useEventCallback<TouchEvent>(nativeEvent => {
    const finger = trackFinger(nativeEvent, touchId);

    if (!finger) {
      return;
    }

    getFingerNewValue({ finger, values: values, source: valueDerived });

    touchId.current = undefined;

    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    stopListening();
  });

  const handleTouchMove = useEventCallback<MouseEvent>(nativeEvent => {
    nativeEvent.stopPropagation();
    const finger = trackFinger(nativeEvent, touchId);

    if (!finger) {
      return;
    }

    // Cancel move in case some other element consumed a mouseup event and it was not fired.
    if (nativeEvent.type === 'mousemove' && nativeEvent.buttons === 0) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      handleTouchEnd(nativeEvent);
      return;
    }

    const { newValue } = getFingerNewValue({
      finger,
      move: true,
      values: values,
      source: valueDerived,
    });

    changeValues(newValue);
  });

  const handleTouchStart = useEventCallback<TouchEvent>(event => {
    event.stopPropagation();

    // If touch-action: none; is not supported we need to prevent the scroll manually.
    if (!doesSupportTouchActionNone()) {
      event.preventDefault();
    }

    const touch = event.changedTouches[0];
    if (touch !== null) {
      // A number that uniquely identifies the current finger in the touch session.
      touchId.current = touch.identifier;
    }
    const finger = trackFinger(event, touchId);
    const { newValue } = getFingerNewValue({ finger, values: values, source: valueDerived });

    changeValues(newValue);

    if (ref.current) {
      ref.current.addEventListener('touchmove', handleTouchMove, { passive: true });
      ref.current.addEventListener('touchend', handleTouchEnd, { passive: true });
    }
  });

  const stopListening = React.useCallback(() => {
    const doc = ownerDocument(ref.current);
    doc.removeEventListener('mousemove', handleTouchMove);
    doc.removeEventListener('mouseup', handleTouchEnd);
    if (ref.current) {
      ref.current.removeEventListener('touchmove', handleTouchMove);
      ref.current.removeEventListener('touchend', handleTouchEnd);
    }
  }, [handleTouchEnd, handleTouchMove]);

  React.useEffect(() => {
    const { current: slider } = ref;
    const passive = doesSupportTouchActionNone();
    slider?.addEventListener('touchstart', handleTouchStart, {
      passive,
    });

    return () => {
      slider?.removeEventListener('touchstart', handleTouchStart);

      stopListening();
    };
  }, [stopListening, handleTouchStart, ref]);

  const handleMouseDown = useEventCallback<MouseEvent>(event => {
    // Only handle left clicks
    if (event.button !== 0) {
      return;
    }

    // Avoid text selection
    event.preventDefault();
    const finger = trackFinger(event, touchId);
    const { newValue } = getFingerNewValue({ finger, values: values, source: valueDerived });

    changeValues(newValue);

    const doc = ownerDocument(ref.current);
    doc.addEventListener('mousemove', handleTouchMove);
    doc.addEventListener('mouseup', handleTouchEnd);
  });

  function changeValues(newValues: number[]) {
    if (!props.disabled?.current) {
      setValueState(newValues);
      throttle(props.onChange, 100)(newValues[0], newValues[1]);
    }
  }

  const handleMinKey = useEventCallback<KeyboardEvent>(event => {
    handleKey(event, true, values, min, max, changeValues);
  });

  const handleMaxKey = useEventCallback<KeyboardEvent>(event => {
    handleKey(event, false, values, min, max, changeValues);
  });

  const trackOffset = min !== max ? valueToPercent(values[0], min, max) : 0;
  const trackLeap = min !== max ? valueToPercent(values[values.length - 1], min, max) - trackOffset : 100;
  const trackStyle = {
    left: `${trackOffset}%`,
    width: `${trackLeap}%`,
  };

  return { handleMouseDown, handleMinKey, handleMaxKey, trackStyle, values, ref };
}

function handleKey(
  event: KeyboardEvent,
  isMin: boolean,
  values: number[],
  min: number,
  max: number,
  setNewValues: (numbers: number[]) => void,
) {
  switch (event.key) {
    case 'ArrowLeft':
    case 'ArrowRight':
      event.preventDefault();
      event.stopPropagation();

      const step = Math.ceil((max - min) / 50);
      const valueIndex = isMin ? 0 : 1;
      const newValue = values[valueIndex] + (event.key === 'ArrowLeft' ? -step : step);
      const newValues = values.slice();
      newValues[valueIndex] = newValue;

      setNewValues(newValues);
      break;
  }
}
